/**
 * RequestServicePage — the primary customer entry point into
 * SocialPulse Services™. Ships the Phase 1 flow:
 *
 *   1. User types a natural-language request (or arrives with prefilled
 *      context via `?prefill=` + `?category=` search params from an
 *      embedded widget).
 *   2. Client calls `/api/sp-services/intake/parse` — Claude Sonnet 4.5
 *      extracts a structured RFS draft. If AI is unavailable (budget
 *      exhausted, network) we degrade to a manual category picker.
 *   3. User reviews / edits the parsed intake, picks the final category,
 *      fills any missing required questions.
 *   4. Submit → `/api/sp-services/rfs` creates the RFS + fires the
 *      matching engine. Redirect to `/services/rfs/:id`.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { http, httpPublic } from "@/lib/api";
import { trackIntel } from "@/lib/analytics";
import useTrackPageView from "@/hooks/useTrackPageView";

function _wordTruncate(text, max) {
  if (!text) return "";
  const s = text.trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > max * 0.5 ? cut.slice(0, lastSpace) : cut) + "…";
}
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Loader2, Sparkles, AlertTriangle, Check } from "lucide-react";

export default function RequestServicePage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [text, setText] = useState(sp.get("prefill") || "");
  const [step, setStep] = useState("compose"); // compose|review|submitting
  const [intake, setIntake] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categorySlug, setCategorySlug] = useState(sp.get("category") || "");
  const [category, setCategory] = useState(null);
  const [answers, setAnswers] = useState({});
  const [err, setErr] = useState("");
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sourcePage = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";

  useTrackPageView("hire_request_view", { entity_type: "hire_page", prefill: !!sp.get("prefill") });

  useEffect(() => {
    httpPublic.get("/sp-services/categories").then(r => setCategories(r.data.categories || []));
  }, []);

  useEffect(() => {
    if (!categorySlug) { setCategory(null); return; }
    httpPublic.get(`/sp-services/categories/${categorySlug}`).then(r => setCategory(r.data));
  }, [categorySlug]);

  const parseWithAI = async () => {
    setErr("");
    if (!text.trim() || text.trim().length < 5) {
      setErr("Please describe what you need in a sentence or two.");
      return;
    }
    setParsing(true);
    try {
      const { data } = await http.post("/sp-services/intake/parse", { text, source_page: sourcePage });
      const i = data.intake;
      trackIntel("hire_intake_parsed", { ai_unavailable: !!i.ai_unavailable, confidence: i.confidence, category_slug: i.category_slug });
      setIntake(i);
      if (i.category_slug) setCategorySlug(i.category_slug);
      // Pre-seed answers from structured fields
      const pre = {};
      if (i.location?.pickup) pre.pickup = i.location.pickup;
      if (i.location?.destination) pre.destination = i.location.destination;
      if (i.location?.city) pre.city = i.location.city;
      if (i.when?.date) pre.date = i.when.date;
      if (i.when?.time) pre.time = i.when.time;
      if (i.quantities?.passengers) pre.passengers = i.quantities.passengers;
      if (i.quantities?.baggage) pre.baggage = i.quantities.baggage;
      if (i.quantities?.guests) pre.guests = i.quantities.guests;
      setAnswers(pre);
      setStep("review");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't parse your request. Try again or fill in manually.");
    } finally {
      setParsing(false);
    }
  };

  const skipAI = () => {
    setIntake({ ai_unavailable: true, description: text, raw_text: text });
    setStep("review");
  };

  const submit = async () => {
    setErr("");
    if (!categorySlug) { setErr("Please pick a service category."); return; }
    // Validate required questions
    const missing = (category?.questions || []).filter(q => q.required && !answers[q.key]);
    if (missing.length) { setErr(`Please answer: ${missing.map(m => m.label).join(", ")}`); return; }
    setSubmitting(true);
    try {
      const payload = {
        category_slug: categorySlug,
        title: (intake?.title || _wordTruncate(text, 60) || category?.name || "Service request").trim(),
        description: text || intake?.description || "",
        structured: answers,
        city: answers.city || intake?.location?.city || null,
        region: intake?.location?.region || null,
        country: intake?.location?.country || null,
        requested_date: answers.date || intake?.when?.date || null,
        requested_time: answers.time || intake?.when?.time || null,
        urgency: intake?.when?.urgency || "flexible",
        source_page: sourcePage,
      };
      const { data } = await http.post("/sp-services/rfs", payload);
      trackIntel("hire_rfs_submitted", { entity_type: "rfs", entity_id: data.rfs.id, category_slug: categorySlug });
      nav(`/hire/rfs/${data.rfs.id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't submit your request. Please try again.");
      setSubmitting(false);
    }
  };

  const rootCategories = useMemo(() => categories.filter(c => !c.parent_slug), [categories]);
  const childCategories = useMemo(() => {
    if (!categorySlug) return categories.filter(c => c.parent_slug);
    const cat = categories.find(c => c.slug === categorySlug);
    if (cat?.parent_slug) return categories.filter(c => c.parent_slug === cat.parent_slug);
    return categories.filter(c => c.parent_slug === categorySlug);
  }, [categories, categorySlug]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="request-service-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Tell us what you need</h1>
        <p className="text-slate-600 mt-2 text-sm">
          Describe your service need in your own words. We'll find qualified providers and share your request with a small, curated group.
        </p>
      </div>

      {step === "compose" && (
        <Card className="p-6 space-y-4">
          <Label htmlFor="req-text" className="text-sm font-medium">What do you need help with?</Label>
          <Textarea
            id="req-text"
            data-testid="request-text-input"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="e.g. I need someone to pick up my parents from Kochi airport around 9 PM November 12 and take them to Pala. They have four suitcases."
            rows={5}
            className="text-base"
          />
          {err && (
            <div className="flex items-start gap-2 text-red-600 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{err}</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={parseWithAI}
              disabled={parsing || !text.trim()}
              data-testid="request-parse-btn"
              className="flex-1"
            >
              {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {parsing ? "Understanding your request..." : "Continue"}
            </Button>
            <Button variant="outline" onClick={skipAI} data-testid="request-skip-ai-btn">
              Fill in manually
            </Button>
          </div>
          <p className="text-xs text-slate-500">Powered by SocialPulse Services — one request goes to up to 5 qualified providers.</p>
        </Card>
      )}

      {step === "review" && (
        <Card className="p-6 space-y-6">
          {intake?.ai_unavailable && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900">
              AI intake not available right now — please pick a category and fill in the details manually.
            </div>
          )}
          {intake?.confidence > 0 && !intake?.ai_unavailable && (
            <div className="flex items-start gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-900">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">We understood your request:</div>
                <div className="text-emerald-800 mt-1">{intake.description}</div>
                {(intake.missing_fields || []).length > 0 && (
                  <div className="mt-2 text-xs">
                    A few more details will help providers give an accurate quote — please fill these in below.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="req-category">Service category</Label>
            <Select value={categorySlug} onValueChange={setCategorySlug}>
              <SelectTrigger id="req-category" data-testid="request-category-select">
                <SelectValue placeholder="Pick a category" />
              </SelectTrigger>
              <SelectContent>
                {rootCategories.map(rc => (
                  <SelectItem key={rc.slug} value={rc.slug} className="font-semibold">
                    {rc.name}
                  </SelectItem>
                ))}
                {categories.filter(c => c.parent_slug).map(c => (
                  <SelectItem key={c.slug} value={c.slug}>
                    &nbsp;&nbsp;→ {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {category?.questions?.length > 0 && (
            <div className="space-y-4">
              {category.questions.map(q => (
                <QuestionField
                  key={q.key}
                  q={q}
                  value={answers[q.key]}
                  onChange={v => setAnswers({ ...answers, [q.key]: v })}
                />
              ))}
            </div>
          )}

          {err && (
            <div className="flex items-start gap-2 text-red-600 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep("compose")}>Back</Button>
            <Button
              onClick={submit}
              disabled={submitting || !categorySlug}
              data-testid="request-submit-btn"
              className="flex-1"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit request
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  if (q.type === "select") {
    return (
      <div className="space-y-1.5">
        <Label>{q.label}{q.required && <span className="text-red-500">*</span>}</Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`q-${q.key}`}><SelectValue placeholder="Choose..." /></SelectTrigger>
          <SelectContent>
            {(q.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        {q.help && <p className="text-xs text-slate-500">{q.help}</p>}
      </div>
    );
  }
  if (q.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-1.5">
        <Label>{q.label}{q.required && <span className="text-red-500">*</span>}</Label>
        <div className="flex flex-wrap gap-1.5">
          {(q.options || []).map(o => {
            const active = arr.includes(o);
            return (
              <button
                key={o}
                type="button"
                data-testid={`q-${q.key}-${o}`}
                onClick={() => onChange(active ? arr.filter(x => x !== o) : [...arr, o])}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
              >
                {o}
              </button>
            );
          })}
        </div>
        {q.help && <p className="text-xs text-slate-500">{q.help}</p>}
      </div>
    );
  }
  if (q.type === "longtext") {
    return (
      <div className="space-y-1.5">
        <Label>{q.label}{q.required && <span className="text-red-500">*</span>}</Label>
        <Textarea data-testid={`q-${q.key}`} value={value || ""} onChange={e => onChange(e.target.value)} rows={3} />
        {q.help && <p className="text-xs text-slate-500">{q.help}</p>}
      </div>
    );
  }
  const inputType = q.type === "number" ? "number" : q.type === "date" ? "date" : q.type === "time" ? "time" : "text";
  return (
    <div className="space-y-1.5">
      <Label>{q.label}{q.required && <span className="text-red-500">*</span>}</Label>
      <Input
        data-testid={`q-${q.key}`}
        type={inputType}
        value={value || ""}
        onChange={e => onChange(inputType === "number" ? Number(e.target.value) : e.target.value)}
      />
      {q.help && <p className="text-xs text-slate-500">{q.help}</p>}
    </div>
  );
}
